// src/services/api/captures.ts
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentData,
  DocumentReference,
  QueryConstraint,
  serverTimestamp,
  onSnapshot, // 新增：即時監聽
  DocumentChange // 新增：文檔變更類型
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FirebaseError } from 'firebase/app';
import { logDev, logInfo } from '../../utils/logger';
import { Capture, CaptureCreateDTO, CaptureUpdateDTO, CaptureFilters, CaptureWithTitle } from '../../types/capture';
import { Task, TaskCategory, TaskPriority, TaskEnergyLevel } from '../../types/task';
import { taskService } from '../../features/task';

// 即時監聽回調接口
interface CaptureSubscriptionCallbacks {
  onSnapshot: (captures: Capture[]) => void;
  onError: (error: Error) => void;
}

// Collection name constant
const COLLECTION = 'captures';

/**
 * Split capture content into title and body
 * Takes the first paragraph as title, rest as body
 * @param content The full capture content
 * @returns Object with title and body properties
 */
const extractTitleAndBody = (content: string): { title: string; body: string } => {
  // 處理空內容的情況
  if (!content || content.trim() === '') {
    return { title: '', body: '' };
  }

  // 使用多種分隔符來偵測段落
  const paragraphSplitters = ['\n\n', '\r\n\r\n', '\n', '\r\n'];
  
  let firstParagraph = content;
  let remainingContent = '';
  
  // 尋找第一個匹配的分隔符
  for (const splitter of paragraphSplitters) {
    if (content.includes(splitter)) {
      const parts = content.split(splitter);
      firstParagraph = parts[0].trim();
      remainingContent = parts.slice(1).join(splitter).trim();
      break;
    }
  }
  
  // 如果沒有找到分隔符，但內容很長，可以取前部分作為標題
  if (remainingContent === '' && firstParagraph.length > 100) {
    const cutoff = firstParagraph.substring(0, 100).lastIndexOf(' ');
    if (cutoff > 30) { // 確保標題有合理長度
      remainingContent = firstParagraph.substring(cutoff + 1);
      firstParagraph = firstParagraph.substring(0, cutoff);
    }
  }
  
  // 確保標題有合理長度
  if (firstParagraph.length > 120) {
    firstParagraph = firstParagraph.substring(0, 117) + '...';
  }
  
  // 如果找到句點，可以在句點處截斷標題
  const sentenceEnd = firstParagraph.indexOf('。');
  if (sentenceEnd > 15 && sentenceEnd < 80) {
    remainingContent = firstParagraph.substring(sentenceEnd + 1) + 
                      (remainingContent ? '\n\n' + remainingContent : '');
    firstParagraph = firstParagraph.substring(0, sentenceEnd + 1);
  }
  
  return {
    title: firstParagraph,
    body: remainingContent
  };
};

/**
 * Convert Firestore document to CaptureWithTitle type
 * @param doc The Firestore document
 * @returns CaptureWithTitle object with extracted title and body
 */
const convertToCaptureWithTitle = (doc: DocumentData): CaptureWithTitle => {
  const data = doc.data();

  // 檢查 timestamp 存在並轉換
  let createdAt = new Date();
  let updatedAt = new Date();
  let convertedAt = undefined;

  if (data.createdAt) {
    if (data.createdAt.toDate) {
      createdAt = data.createdAt.toDate();
    } else if (data.createdAt instanceof Date) {
      createdAt = data.createdAt;
    }
  }

  if (data.updatedAt) {
    if (data.updatedAt.toDate) {
      updatedAt = data.updatedAt.toDate();
    } else if (data.updatedAt instanceof Date) {
      updatedAt = data.updatedAt;
    }
  }

  if (data.convertedAt) {
    if (data.convertedAt.toDate) {
      convertedAt = data.convertedAt.toDate();
    } else if (data.convertedAt instanceof Date) {
      convertedAt = data.convertedAt;
    }
  }

  // 提取標題和內容
  const { title, body } = extractTitleAndBody(data.content);

  // 轉換 Firestore 文檔為 CaptureWithTitle 物件
  return {
    id: doc.id,
    content: data.content, // 保留原始內容
    title: title,          // 提取的標題
    body: body,            // 提取的內容
    processed: data.processed || false,
    userId: data.userId,
    
    // 統一的關聯項目字段
    relatedItemId: data.relatedItemId || undefined,
    relatedItemType: data.relatedItemType || undefined,
    relatedItemTitle: data.relatedItemTitle || undefined,
    
    // Legacy TimeBox 字段 (for backward compatibility)
    timeBoxId: data.timeBoxId || undefined,
    timeBoxTitle: data.timeBoxTitle || undefined,
    
    // Event 字段
    eventId: data.eventId || undefined,
    eventTitle: data.eventTitle || undefined,
    
    convertedToId: data.convertedToId || undefined,
    convertedToType: data.convertedToType || undefined,
    createdAt,
    updatedAt,
    convertedAt,
    source: data.source || undefined,
  };
};

/**
 * Convert Firestore document to Capture type (for backward compatibility)
 * @param doc The Firestore document
 * @returns Capture object
 */
const convertToCapture = (doc: DocumentData): Capture => {
  const data = doc.data();

  // 檢查 timestamp 存在並轉換
  let createdAt = new Date();
  let updatedAt = new Date();
  let convertedAt = undefined;

  if (data.createdAt) {
    if (data.createdAt.toDate) {
      createdAt = data.createdAt.toDate();
    } else if (data.createdAt instanceof Date) {
      createdAt = data.createdAt;
    }
  }

  if (data.updatedAt) {
    if (data.updatedAt.toDate) {
      updatedAt = data.updatedAt.toDate();
    } else if (data.updatedAt instanceof Date) {
      updatedAt = data.updatedAt;
    }
  }

  if (data.convertedAt) {
    if (data.convertedAt.toDate) {
      convertedAt = data.convertedAt.toDate();
    } else if (data.convertedAt instanceof Date) {
      convertedAt = data.convertedAt;
    }
  }

  // 轉換 Firestore 文檔為 Capture 物件
  return {
    id: doc.id,
    content: data.content,
    processed: data.processed || false,
    userId: data.userId,
    
    // 統一的關聯項目字段
    relatedItemId: data.relatedItemId || undefined,
    relatedItemType: data.relatedItemType || undefined,
    relatedItemTitle: data.relatedItemTitle || undefined,
    
    // Legacy TimeBox 字段 (for backward compatibility)
    timeBoxId: data.timeBoxId || undefined,
    timeBoxTitle: data.timeBoxTitle || undefined,
    
    // Event 字段
    eventId: data.eventId || undefined,
    eventTitle: data.eventTitle || undefined,
    
    convertedToId: data.convertedToId || undefined,
    convertedToType: data.convertedToType || undefined,
    createdAt,
    updatedAt,
    convertedAt,
    source: data.source || undefined,
  };
};

/**
 * Create a new capture
 * @param captureData The capture data to create
 * @returns The created capture with ID and extracted title/body
 */
export const createCapture = async (captureData: CaptureCreateDTO): Promise<CaptureWithTitle> => {

  try {
    // 處理可能為 null 的欄位，確保它們符合 Capture 類型
    const { 
      timeBoxId, 
      timeBoxTitle, 
      eventId, 
      eventTitle,
      relatedItemId,
      relatedItemType,
      relatedItemTitle,
      ...restData 
    } = captureData;

    // 準備要寫入 Firestore 的資料
    const captureDoc = {
      content: captureData.content,
      userId: captureData.userId,
      
      // 統一的關聯項目字段 (preferred)
      relatedItemId: relatedItemId || null,
      relatedItemType: relatedItemType || null,
      relatedItemTitle: relatedItemTitle || null,
      
      // Legacy TimeBox 字段 (for backward compatibility)
      timeBoxId: timeBoxId || null,
      timeBoxTitle: timeBoxTitle || null,
      
      // Event 字段
      eventId: eventId || null,
      eventTitle: eventTitle || null,
      
      processed: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };


    // 改進寫入後的取得操作
    const docRef = await addDoc(collection(db, COLLECTION), captureDoc);

    // 等待一小段時間確保服務器完成寫入
    await new Promise(resolve => setTimeout(resolve, 500));

    // 取得剛新增的文件
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Failed to retrieve created capture');
    }

    // 轉換為 CaptureWithTitle 類型
    const result = convertToCaptureWithTitle(docSnap);
    return result;
  } catch (error: unknown) {
    console.error('Error creating capture:', error);

    if (error instanceof FirebaseError) {
      // Firebase 特定錯誤處理
      switch (error.code) {
        case 'unavailable':
          throw new Error('Firebase 服務暫時不可用，請稍後再試');
        case 'permission-denied':
          throw new Error('沒有權限執行此操作，請檢查授權設置');
        case 'resource-exhausted':
          throw new Error('達到了資源限制，請稍後再試');
        case 'failed-precondition':
          throw new Error('操作條件不滿足，請檢查應用狀態');
        default:
          throw new Error(`Firebase 錯誤：${error.message}`);
      }
    } else if (error instanceof TypeError || error instanceof SyntaxError) {
      // 程式碼或語法錯誤
      throw new Error(`程式錯誤：${(error as Error).message}`);
    } else if (error instanceof Error && error.message.includes('timeout')) {
      // 超時錯誤
      console.error('錯誤詳情:', error.message, error.stack);
      throw new Error('操作超時，請檢查網路連接並重試');
    } else if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
        console.error('網絡錯誤:', error);
        throw new Error('網絡連接問題 - 請檢查您的網絡連接並重試');
      }
      // 其他 Error 類型
      console.error('錯誤詳情:', error.message, error.stack);
      throw error; // 保留原始錯誤和堆棧
    } else {
      // 完全未知的錯誤
      throw new Error('建立捕獲時發生未知錯誤');
    }
  }
};

/**
 * Get all captures with optional filters
 * @param filters Optional filters
 * @returns Array of captures with extracted title and body
 */
export const getCaptures = async (
  filters?: Partial<CaptureFilters>
): Promise<CaptureWithTitle[]> => {
  
  try {
    // 建立基本查詢
    let capturesQuery = collection(db, COLLECTION);
    let constraints = [];
    
    // 添加過濾條件
    if (filters) {
      if (filters.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }
      
      if (filters.processed !== undefined) {
        constraints.push(where('processed', '==', filters.processed));
      }
      
      if (filters.timeBoxId) {
        constraints.push(where('timeBoxId', '==', filters.timeBoxId));
      }
      
      if (filters.eventId) {
        constraints.push(where('eventId', '==', filters.eventId));
      }
      
      if (filters.relatedItemId) {
        constraints.push(where('relatedItemId', '==', filters.relatedItemId));
      }
      
      if (filters.relatedItemType) {
        constraints.push(where('relatedItemType', '==', filters.relatedItemType));
      }
      
      // 時間過濾
      if (filters.createdAfter) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.createdAfter)));
      }
      
      if (filters.createdBefore) {
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.createdBefore)));
      }
    }
    
    // 添加排序（總是按創建時間降序排列）
    constraints.push(orderBy('createdAt', 'desc'));
    
    // 分頁支援
    if (filters?.limit) {
      constraints.push(limit(filters.limit));
    }
    
    if (filters?.startAfter) {
      constraints.push(startAfter(filters.startAfter));
    }
    
    // 執行查詢
    const q = query(capturesQuery, ...constraints);
    
    const querySnapshot = await getDocs(q);
    
    // 轉換結果
    const results = querySnapshot.docs.map(doc => convertToCaptureWithTitle(doc));
    
    return results;
  } catch (error) {
    console.error('Error fetching captures:', error);
    throw new Error(`Failed to fetch captures: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get captures with pagination support - 專門用於分頁查詢
 * @param filters 查詢過濾條件
 * @returns 包含 captures、hasMore 和 lastDoc 的分頁結果
 */
export const getCapturesPaginated = async (
  filters?: Partial<CaptureFilters>
): Promise<{
  captures: CaptureWithTitle[];
  hasMore: boolean;
  lastDoc?: DocumentData;
  nextCursor?: string;
}> => {
  
  try {
    // 預設限制為 50 筆
    const defaultLimit = 50;
    const queryLimit = filters?.limit || defaultLimit;
    
    // 建立查詢（重用現有邏輯）
    let capturesQuery = collection(db, COLLECTION);
    let constraints = [];
    
    // 添加過濾條件（重用現有邏輯）
    if (filters) {
      if (filters.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }
      
      if (filters.processed !== undefined) {
        constraints.push(where('processed', '==', filters.processed));
      }
      
      if (filters.timeBoxId) {
        constraints.push(where('timeBoxId', '==', filters.timeBoxId));
      }
      
      if (filters.eventId) {
        constraints.push(where('eventId', '==', filters.eventId));
      }
      
      if (filters.relatedItemId) {
        constraints.push(where('relatedItemId', '==', filters.relatedItemId));
      }
      
      if (filters.relatedItemType) {
        constraints.push(where('relatedItemType', '==', filters.relatedItemType));
      }
      
      // 時間過濾
      if (filters.createdAfter) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.createdAfter)));
      }
      
      if (filters.createdBefore) {
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.createdBefore)));
      }
    }
    
    // 排序和分頁
    constraints.push(orderBy('createdAt', 'desc'));
    constraints.push(limit(queryLimit + 1)); // +1 用來檢查是否還有更多資料
    
    if (filters?.startAfter) {
      constraints.push(startAfter(filters.startAfter));
    }
    
    // 執行查詢
    const q = query(capturesQuery, ...constraints);
    
    const querySnapshot = await getDocs(q);
    
    // 處理分頁邏輯
    const docs = querySnapshot.docs;
    const hasMore = docs.length > queryLimit;
    const actualDocs = hasMore ? docs.slice(0, queryLimit) : docs;
    const lastDoc = actualDocs.length > 0 ? actualDocs[actualDocs.length - 1] : undefined;
    
    // 轉換結果
    const captures = actualDocs.map(doc => convertToCaptureWithTitle(doc));
    
    // 生成下一頁游標（使用最後一個文檔的創建時間）
    let nextCursor: string | undefined;
    if (lastDoc && hasMore) {
      const lastDocData = lastDoc.data();
      if (lastDocData.createdAt) {
        nextCursor = lastDocData.createdAt.toMillis().toString();
      }
    }
    
    
    return {
      captures,
      hasMore,
      lastDoc,
      nextCursor
    };
  } catch (error) {
    console.error('Error fetching paginated captures:', error);
    throw new Error(`Failed to fetch paginated captures: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get captures by TimeBox ID
 * @param timeBoxId The TimeBox ID to filter by
 * @returns Array of captures with title and body
 */
export const getCapturesByTimeBoxId = async (timeBoxId: string): Promise<CaptureWithTitle[]> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('timeBoxId', '==', timeBoxId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertToCaptureWithTitle(doc));
  } catch (error) {
    console.error('Error fetching captures by TimeBox ID:', error);
    throw new Error('Failed to fetch captures by TimeBox ID');
  }
};

/**
 * Get captures by Event ID
 * @param eventId The Event ID to filter by
 * @returns Array of captures with title and body
 */
export const getCapturesByEventId = async (eventId: string): Promise<CaptureWithTitle[]> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('eventId', '==', eventId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertToCaptureWithTitle(doc));
  } catch (error) {
    console.error('Error fetching captures by Event ID:', error);
    throw new Error('Failed to fetch captures by Event ID');
  }
};

/**
 * Get captures by related item (unified approach)
 * @param relatedItemId The related item ID to filter by
 * @param relatedItemType The type of related item ('timeBox' or 'event')
 * @returns Array of captures with title and body
 */
export const getCapturesByRelatedItem = async (
  relatedItemId: string, 
  relatedItemType: 'timeBox' | 'event'
): Promise<CaptureWithTitle[]> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('relatedItemId', '==', relatedItemId),
      where('relatedItemType', '==', relatedItemType),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => convertToCaptureWithTitle(doc));
  } catch (error) {
    console.error('Error fetching captures by related item:', error);
    throw new Error('Failed to fetch captures by related item');
  }
};

/**
 * Update a capture
 * @param id The capture ID to update
 * @param updateData The data to update
 * @returns The updated capture with title and body
 */
export const updateCapture = async (
  id: string,
  updateData: CaptureUpdateDTO
): Promise<CaptureWithTitle> => {
  try {
    const captureRef = doc(db, COLLECTION, id);
    const captureSnap = await getDoc(captureRef);

    if (!captureSnap.exists()) {
      throw new Error(`Capture with ID ${id} not found`);
    }

    // 不從客戶端接收 userId，而是從現有文件獲取
    const currentUserId = captureSnap.data().userId;

    // 在真實環境中，這裡應該檢查當前登入用戶是否有權限
    // if (currentUser.uid !== currentUserId) {
    //   throw new Error('Unauthorized');
    // }

    // 準備更新的資料
    const updateFields = {
      ...updateData,
      updatedAt: serverTimestamp(),
    };

    // 更新文件
    await updateDoc(captureRef, updateFields);

    // 取得更新後的文件
    const updatedSnap = await getDoc(captureRef);

    if (!updatedSnap.exists()) {
      throw new Error('Failed to retrieve updated capture');
    }

    // 轉換為 CaptureWithTitle 類型並返回
    return convertToCaptureWithTitle(updatedSnap);
  } catch (error) {
    console.error('Error updating capture:', error);
    throw new Error('Failed to update capture');
  }
};

/**
 * Delete a capture
 * @param id The capture ID to delete
 */
export const deleteCapture = async (id: string): Promise<void> => {
  try {
    const captureRef = doc(db, COLLECTION, id);
    await deleteDoc(captureRef);
  } catch (error) {
    console.error('Error deleting capture:', error);
    throw new Error('Failed to delete capture');
  }
};


/**
 * Convert a capture to a task
 * @param captureId The capture ID to convert
 * @param formData The task data from form
 * @returns The created task and updated capture
 */
export const convertCaptureToTask = async (
  captureId: string,
  formData: {
    title: string;
    description?: string;
    notes?: string;
    targetDate?: string;
    category?: TaskCategory;
    priority?: TaskPriority;
    energyLevel?: TaskEnergyLevel;
    estimatedTime?: number;
    location?: string;
    userId: string;
  }
): Promise<{ createdTask: Task; updatedCapture: CaptureWithTitle }> => {
  try {
    // 1. 獲取 capture 詳情，確認存在且有權限
    const capture = await getCaptureById(captureId);
    if (!capture) {
      throw new Error('Capture not found');
    }


    // 2. 將表單數據轉換為 Task 創建所需的格式
    // 只取出需要的字段，避免未定義的字段傳遞
    const taskCreationData = {
      title: formData.title.trim(),
      notes: formData.notes?.trim() || formData.description?.trim(), // 支持舊版的 description 字段
      targetDate: formData.targetDate,
      category: formData.category,
      priority: formData.priority,
      energyLevel: formData.energyLevel,
      estimatedTime: formData.estimatedTime,
      location: formData.location?.trim(),
      userId: formData.userId,
      originCaptureId: captureId  // 明確設置來源 captureId
    };


    // 3. 使用 taskService 創建實際任務
    const createdTask = await taskService.createTask(taskCreationData);
    

    // 4. 更新 capture 的狀態為已處理
    const updatedCapture = await updateCapture(captureId, {
      processed: true,
      convertedToId: createdTask.id,
      convertedToType: 'TASK',
      convertedAt: new Date(),
    });


    return {
      createdTask,
      updatedCapture,
    };
  } catch (error) {
    console.error('Error converting capture to task:', error);
    throw new Error('Failed to convert capture to task');
  }
};

/**
 * Get capture by ID
 * @param id The capture ID
 * @returns The capture with title and body or null if not found
 */
export const getCaptureById = async (id: string): Promise<CaptureWithTitle | null> => {
  try {
    const captureRef = doc(db, COLLECTION, id);
    const captureSnap = await getDoc(captureRef);

    if (!captureSnap.exists()) {
      return null;
    }

    return convertToCaptureWithTitle(captureSnap);
  } catch (error) {
    console.error('Error fetching capture:', error);
    return null;
  }
};

/**
 * Get multiple captures by IDs (batch operation)
 * @param ids Array of capture IDs
 * @returns Array of captures (only existing ones)
 */
export const getCapturesByIds = async (ids: string[]): Promise<CaptureWithTitle[]> => {
  if (!ids.length) return [];
  
  try {
    // 使用 Promise.allSettled 來處理部分失敗的情況
    const capturePromises = ids.map(id => getCaptureById(id));
    const results = await Promise.allSettled(capturePromises);
    
    // 只返回成功獲取的 captures
    return results
      .filter((result): result is PromiseFulfilledResult<CaptureWithTitle | null> => 
        result.status === 'fulfilled' && result.value !== null)
      .map(result => result.value!);
  } catch (error) {
    console.error('Error fetching captures by IDs:', error);
    throw new Error('Failed to fetch captures by IDs');
  }
};

// 為了向後兼容，導出原始的轉換函數
export const getLegacyCaptures = async (
  filters?: Partial<CaptureFilters>
): Promise<Capture[]> => {
  
  try {
    // 建立基本查詢
    let capturesQuery = collection(db, COLLECTION);
    let constraints = [];
    
    // 添加過濾條件
    if (filters) {
      if (filters.userId) {
        constraints.push(where('userId', '==', filters.userId));
      }
      
      if (filters.processed !== undefined) {
        constraints.push(where('processed', '==', filters.processed));
      }
      
      if (filters.timeBoxId) {
        constraints.push(where('timeBoxId', '==', filters.timeBoxId));
      }
      
      if (filters.eventId) {
        constraints.push(where('eventId', '==', filters.eventId));
      }
      
      if (filters.relatedItemId) {
        constraints.push(where('relatedItemId', '==', filters.relatedItemId));
      }
      
      if (filters.relatedItemType) {
        constraints.push(where('relatedItemType', '==', filters.relatedItemType));
      }
      
      // 時間過濾
      if (filters.createdAfter) {
        constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.createdAfter)));
      }
      
      if (filters.createdBefore) {
        constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.createdBefore)));
      }
    }
    
    // 添加排序（總是按創建時間降序排列）
    constraints.push(orderBy('createdAt', 'desc'));
    
    // 執行查詢
    const q = query(capturesQuery, ...constraints);
    const querySnapshot = await getDocs(q);
    
    // 轉換結果
    return querySnapshot.docs.map(doc => convertToCapture(doc));
  } catch (error) {
    console.error('Error fetching legacy captures:', error);
    throw new Error(`Failed to fetch legacy captures: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * 訂閱 Capture 變更的即時監聽
 * @param userId 用戶 ID
 * @param callbacks 回調函數
 * @param options 監聽選項
 * @returns 取消訂閱的函數
 */
export const subscribeToCaptures = async (
  userId: string,
  callbacks: CaptureSubscriptionCallbacks,
  options?: {
    limit?: number;
    includeProcessed?: boolean;
  }
): Promise<() => void> => {
  try {
    // 創建基本查詢，按用戶ID過濾，按創建時間排序
    let constraints: QueryConstraint[] = [
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    ];

    // 如果不包含已處理的項目，添加過濾條件
    if (options?.includeProcessed === false) {
      constraints.push(where('processed', '==', false));
    }
    
    // 添加 limit 約束（如果有提供）
    if (options?.limit) {
      constraints.push(limit(options.limit));
    }

    const q = query(collection(db, COLLECTION), ...constraints);


    // 使用 onSnapshot 監聽查詢結果變化
    let isInitialLoad = true;

    const unsubscribe = onSnapshot(
      q,
      {
        // 啟用本地快取
        includeMetadataChanges: false
      },
      (querySnapshot) => {
        try {
          const captures: Capture[] = [];

          // 轉換所有文檔到 Capture 數組
          querySnapshot.forEach((doc) => {
            captures.push(convertToCapture(doc));
          });

          // 區分初始載入和真實變更
          if (isInitialLoad) {
            isInitialLoad = false;
          } else {
            // 只在真正有變更時記錄
            const changes = querySnapshot.docChanges();
            if (changes.length > 0) {
            }
          }

          // 回調通知 Capture 更新
          callbacks.onSnapshot(captures);

        } catch (error) {
          console.error('處理即時 Capture 更新失敗:', error);
          callbacks.onError(error instanceof Error ? error : new Error('處理 Capture 更新失敗'));
        }
      },
      (error) => {
        // 檢查是否為預期的離線錯誤
        const isExpectedOfflineError =
          (error as FirebaseError).code === 'unavailable' ||
          (error as FirebaseError).code === 'failed-precondition' ||
          error.message?.includes('Failed to get document') ||
          error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message?.includes('ERR_NETWORK_CHANGED');

        if (isExpectedOfflineError) {
          // 離線錯誤，只記錄但降低日誌級別
        } else {
          // 非預期的錯誤，正常記錄
          console.error('Capture Firestore 即時監聽錯誤:', error);
        }
        
        // 無論如何都要回調錯誤，讓上層決定如何處理
        callbacks.onError(error instanceof Error ? error : new Error('即時監聽連接失敗'));
      }
    );

    return unsubscribe;

  } catch (error) {
    console.error('啟動 Capture 即時監聽失敗:', error);
    throw error instanceof Error ? error : new Error('無法啟動即時監聽');
  }
};

/**
 * 訂閱特定處理狀態的 Capture 變更
 * @param userId 用戶 ID
 * @param processed 處理狀態
 * @param callbacks 回調函數
 * @returns 取消訂閱的函數
 */
export const subscribeToProcessedCaptures = async (
  userId: string,
  processed: boolean,
  callbacks: CaptureSubscriptionCallbacks
): Promise<() => void> => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId),
      where('processed', '==', processed),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q,
      (querySnapshot) => {
        const captures: Capture[] = [];
        querySnapshot.forEach((doc) => {
          captures.push(convertToCapture(doc));
        });
        callbacks.onSnapshot(captures);
      },
      (error) => {
        // 檢查是否為預期的離線錯誤
        const isExpectedOfflineError =
          (error as FirebaseError).code === 'unavailable' ||
          (error as FirebaseError).code === 'failed-precondition' ||
          error.message?.includes('Failed to get document') ||
          error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
          error.message?.includes('ERR_NETWORK_CHANGED');

        if (isExpectedOfflineError) {
        } else {
          console.error('監聽特定狀態 Capture 失敗:', error);
        }
        
        callbacks.onError(error instanceof Error ? error : new Error('監聽特定狀態 Capture 失敗'));
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('訂閱特定狀態 Capture 失敗:', error);
    throw error;
  }
};

/**
 * 分析 Capture 內容並提取可執行任務
 * @param captureId Capture ID
 * @param captureContent Capture 內容
 * @param userId 用戶 ID
 * @returns AI 分析結果
 */
export const analyzeCaptureForTasks = async (
  captureId: string,
  captureContent: string,
  userId: string
): Promise<{
  shouldConvert: boolean;
  reasoning: string;
  userMessage: string;
  extractedTasks: Array<{
    title: string;
    notes?: string;
    category?: TaskCategory;
    priority?: TaskPriority;
    energyLevel?: TaskEnergyLevel;
    estimatedTime?: number;
    confidence: number;
    targetDate?: string;
  }>;
  overallConfidence: number;
}> => {
  try {

    // 導入 Firebase Functions
    const { httpsCallable } = await import('firebase/functions');
    const { functions } = await import('../../config/firebase');
    
    // 建立 Firebase Function 調用
    const analyzeCaptureForTasksFunction = httpsCallable(functions, 'analyzeCaptureForTasks');
    
    // 準備請求參數
    const requestData = {
      captureId,
      captureContent: captureContent.trim(),
      userId,
      currentDate: new Date().toISOString().split('T')[0] // YYYY-MM-DD 格式
    };

    logDev('CaptureAnalysis', '發送分析請求', { 
      captureId, 
      currentDate: requestData.currentDate,
      contentLength: captureContent.length 
    });

    // 呼叫 Firebase Function
    const result = await analyzeCaptureForTasksFunction(requestData);
    
    // 提取回應數據
    interface AnalysisResultData {
      shouldConvert: boolean;
      reasoning: string;
      userMessage: string;
      extractedTasks: Array<{
        title: string;
        notes?: string;
        category?: string;
        priority?: string;
        energyLevel?: string;
        estimatedTime?: number;
        confidence: number;
        targetDate?: string;
        [key: string]: unknown;
      }>;
      overallConfidence: number;
      [key: string]: unknown;
    }
    const analysisResult = result.data as AnalysisResultData;

    logInfo('CaptureAnalysis', '分析結果', {
      shouldConvert: analysisResult.shouldConvert,
      extractedTasksCount: analysisResult.extractedTasks?.length || 0,
      overallConfidence: analysisResult.overallConfidence
    });

    // 驗證回應格式
    if (typeof analysisResult.shouldConvert !== 'boolean') {
      throw new Error('Invalid response format: shouldConvert must be boolean');
    }

    if (!Array.isArray(analysisResult.extractedTasks)) {
      throw new Error('Invalid response format: extractedTasks must be array');
    }

    // 轉換 AI 回應的分類格式到系統格式
    const convertedResult = {
      ...analysisResult,
      extractedTasks: analysisResult.extractedTasks.map((task) => ({
        ...task,
        // 轉換 AI 使用的分類到系統類型
        category: convertAICategoryToSystem(task.category ?? ''),
        priority: convertAIPriorityToSystem(task.priority ?? ''),
        energyLevel: convertAIEnergyLevelToSystem(task.energyLevel ?? '')
      }))
    };

    return convertedResult;

  } catch (error: unknown) {
    console.error('Capture 分析失敗:', error);

    if (error instanceof Error) {
      // Firebase Functions 特定錯誤處理
      if (error.message.includes('unauthenticated')) {
        throw new Error('用戶未經驗證，請重新登入');
      } else if (error.message.includes('invalid-argument')) {
        throw new Error('分析參數無效，請檢查 Capture 內容');
      } else if (error.message.includes('internal')) {
        throw new Error('AI 分析服務暫時不可用，請稍後再試');
      } else if (error.message.includes('timeout')) {
        throw new Error('分析請求超時，請稍後再試');
      } else if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
        throw new Error('網絡連接問題，請檢查網絡後重試');
      }
      
      // 保留原始錯誤信息
      throw new Error(`Capture 分析失敗: ${error.message}`);
    } else {
      // 完全未知的錯誤
      throw new Error('Capture 分析時發生未知錯誤，請稍後再試');
    }
  }
};

/**
 * 轉換 AI 回應的分類格式到系統格式
 */
const convertAICategoryToSystem = (aiCategory: string | null | undefined): TaskCategory => {
  if (!aiCategory) return 'personal';
  
  // AI 現在直接輸出正確的格式，只需要驗證有效性
  const validCategories: TaskCategory[] = ['work', 'family', 'health', 'social', 'personal'];
  
  if (validCategories.includes(aiCategory as TaskCategory)) {
    return aiCategory as TaskCategory;
  }
  
  return 'personal'; // 只有無效值才預設為 personal
};

/**
 * 轉換 AI 回應的優先度格式到系統格式
 */
const convertAIPriorityToSystem = (aiPriority: string): TaskPriority => {
  switch (aiPriority) {
    case 'HIGH':
      return '1';
    case 'MEDIUM':
      return '2';
    case 'LOW':
      return '3';
    default:
      return '2';
  }
};

/**
 * 轉換 AI 回應的能量需求格式到系統格式
 */
const convertAIEnergyLevelToSystem = (aiEnergyLevel: string): TaskEnergyLevel => {
  switch (aiEnergyLevel) {
    case 'HIGH':
      return '1';
    case 'MEDIUM':
      return '2';
    case 'LOW':
      return '3';
    default:
      return '2';
  }
};